/**
 * Government Forms Admin Page
 *
 * Shows all government forms and which template fields are tagged to each.
 * Admins can remove tags here or add new field→form associations.
 * Editing formTags uses PATCH /api/admin/field-config/fields/:id.
 */

import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { X, Plus, FileText, ChevronDown, ChevronRight, Check } from "lucide-react";

interface GovernmentForm {
  id: string;
  title: string;
  agency: string;
  deadline?: string;
  notes?: string;
}

interface TemplateField {
  fieldId: string;
  fieldName: string;
  fieldKey: string;
  fieldType: string;
  formTags: string[] | null;
  sectionId: string;
  sectionName: string;
  categoryId: string;
  categoryName: string;
  categorySlug: string;
}

export default function GovernmentFormsAdmin() {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [addOpen, setAddOpen] = useState<Record<string, boolean>>({});

  const { data: forms = [] } = useQuery<GovernmentForm[]>({
    queryKey: ["/api/admin/government-forms"],
  });

  const { data: allFields = [], isLoading } = useQuery<TemplateField[]>({
    queryKey: ["/api/admin/government-forms/tagged-fields"],
  });

  const patchMutation = useMutation({
    mutationFn: async ({ fieldId, formTags }: { fieldId: string; formTags: string[] }) => {
      const res = await apiRequest("PATCH", `/api/admin/field-config/fields/${fieldId}`, { formTags });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/government-forms/tagged-fields"] });
    },
    onError: (err: Error) => {
      toast({ title: "Failed to update", description: err.message, variant: "destructive" });
    },
  });

  const removeTag = (field: TemplateField, formId: string) => {
    const next = (field.formTags ?? []).filter(t => t !== formId);
    patchMutation.mutate({ fieldId: field.fieldId, formTags: next });
  };

  const addTag = (field: TemplateField, formId: string) => {
    const merged = (field.formTags ?? []).concat(formId);
    const next = merged.filter((v, i) => merged.indexOf(v) === i);
    patchMutation.mutate({ fieldId: field.fieldId, formTags: next });
  };

  const toggle = (id: string) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  // Fields tagged to a given form
  const fieldsForForm = (formId: string) =>
    allFields.filter(f => f.formTags?.includes(formId));

  // Fields NOT tagged to a given form (candidates for adding)
  const untaggedForForm = (formId: string) =>
    allFields.filter(f => !(f.formTags ?? []).includes(formId));

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Government Forms</h1>
        <p className="text-muted-foreground mt-1">
          Manage which estate planning fields are required for each Ontario government form.
          Tagged fields show a <span className="text-[11px] px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: 'hsl(168 76% 42% / 0.15)', color: 'hsl(168 76% 36%)' }}>Gov Form</span> badge in the client portal.
        </p>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground py-8 text-center">Loading fields…</div>
      ) : (
        <div className="space-y-3">
          {forms.map(form => {
            const tagged = fieldsForForm(form.id);
            const untagged = untaggedForForm(form.id);
            const open = expanded[form.id] ?? true;

            return (
              <div key={form.id} className="border rounded-lg bg-card overflow-hidden">
                {/* Form header */}
                <button
                  className="w-full flex items-start gap-3 p-4 text-left hover:bg-muted/30 transition-colors"
                  onClick={() => toggle(form.id)}
                >
                  <div className="mt-0.5 text-primary shrink-0">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-foreground">{form.title}</span>
                      <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                        {form.agency}
                      </Badge>
                      {form.deadline && (
                        <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                          Due: {form.deadline}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground ml-auto">
                        {tagged.length} field{tagged.length !== 1 ? "s" : ""} tagged
                      </span>
                    </div>
                    {form.notes && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{form.notes}</p>
                    )}
                  </div>
                  {open ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />}
                </button>

                {/* Tagged fields list */}
                {open && (
                  <div className="border-t px-4 pb-4 pt-3">
                    {tagged.length === 0 ? (
                      <p className="text-xs text-muted-foreground italic mb-3">No fields tagged yet.</p>
                    ) : (
                      <div className="space-y-1 mb-3">
                        {tagged.map(field => (
                          <div
                            key={field.fieldId}
                            className="flex items-center gap-2 text-xs py-1 px-2 rounded bg-muted/40 group"
                          >
                            <span className="text-muted-foreground shrink-0">{field.categoryName} › {field.sectionName}</span>
                            <span className="font-medium text-foreground">{field.fieldName}</span>
                            <span className="text-[10px] text-muted-foreground font-mono ml-1 opacity-60">{field.fieldKey}</span>
                            <button
                              className="ml-auto shrink-0 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                              title="Remove from this form"
                              onClick={() => removeTag(field, form.id)}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add field button */}
                    <Popover
                      open={addOpen[form.id] ?? false}
                      onOpenChange={v => setAddOpen(a => ({ ...a, [form.id]: v }))}
                    >
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
                          <Plus className="w-3 h-3" />
                          Add field
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 p-2 max-h-64 overflow-y-auto" align="start">
                        {untagged.length === 0 ? (
                          <p className="text-xs text-muted-foreground p-2">All fields are already tagged.</p>
                        ) : (
                          <AddFieldList
                            fields={untagged}
                            onSelect={field => {
                              addTag(field, form.id);
                              setAddOpen(a => ({ ...a, [form.id]: false }));
                            }}
                          />
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AddFieldList({
  fields,
  onSelect,
}: {
  fields: TemplateField[];
  onSelect: (f: TemplateField) => void;
}) {
  const [search, setSearch] = useState("");

  // Group by category > section
  const filtered = search
    ? fields.filter(
        f =>
          f.fieldName.toLowerCase().includes(search.toLowerCase()) ||
          f.sectionName.toLowerCase().includes(search.toLowerCase()) ||
          f.categoryName.toLowerCase().includes(search.toLowerCase()),
      )
    : fields;

  // Group
  const groups: Record<string, { sectionName: string; fields: TemplateField[] }> = {};
  for (const f of filtered) {
    const key = `${f.categoryName} › ${f.sectionName}`;
    if (!groups[key]) groups[key] = { sectionName: key, fields: [] };
    groups[key].fields.push(f);
  }

  return (
    <div>
      <input
        autoFocus
        placeholder="Search fields…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full text-xs px-2 py-1.5 border rounded mb-2 outline-none focus:ring-1 focus:ring-primary"
      />
      {Object.entries(groups).map(([key, group]) => (
        <div key={key} className="mb-2">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide px-1 mb-0.5">
            {key}
          </div>
          {group.fields.map(f => (
            <button
              key={f.fieldId}
              className="w-full text-left text-xs px-2 py-1 rounded hover:bg-muted flex items-center gap-1.5"
              onClick={() => onSelect(f)}
            >
              <span className="font-medium">{f.fieldName}</span>
              <span className="text-[10px] text-muted-foreground font-mono opacity-60">{f.fieldKey}</span>
            </button>
          ))}
        </div>
      ))}
      {filtered.length === 0 && (
        <p className="text-xs text-muted-foreground px-2 py-1">No matching fields.</p>
      )}
    </div>
  );
}
